/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, April 18th 2022, 1:04:36 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import Checkbox from "@mui/material/Checkbox";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import { Key } from "lib/types";
import TableRow from "@mui/material/TableRow";
import { DataTableHead } from "./DataTableHead";

export const TableList = <OSObjectType,>({
  headCells,
  data,
  selectedRowsKeys,
  rowCount,
  dense,
  uniqueKey,
  allowRowSelection = true,
  showCellBorders = true,
  showTableHead = true,
  renderItemAvatar = null,
  renderRowActions,
  enableRowItemActions = true,
  columnKeys,
  emptyRows,
  order,
  orderBy,
  handleRequestSort,
  handleSelectAllClick,
  handleSelectRow,
  allRowsSelected,
  rowIsSelected,
  cellRenderProps = {},
}) => {
  return (
    <TableContainer>
      <Table aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
        {showTableHead && (
          <DataTableHead<OSObjectType>
            headCells={headCells}
            numSelected={selectedRowsKeys.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rowCount}
            allowSelect={allowRowSelection}
            allRowsSelected={allRowsSelected()}
            showAvatar={Boolean(renderItemAvatar)}
            enableRowItemActions={enableRowItemActions}
          />
        )}
        <TableBody>
          {data.map((row, index) => {
            const isItemSelected = rowIsSelected(row[uniqueKey]);
            const labelId = `enhanced-table-checkbox-${index}`;
            const borderStyle: React.CSSProperties = showCellBorders ? {} : { border: "none" };

            return (
              <TableRow
                hover
                role="checkbox"
                aria-checked={isItemSelected}
                tabIndex={-1}
                key={row[uniqueKey].toString()}
                selected={isItemSelected}
              >
                {allowRowSelection ? (
                  <TableCell
                    onClick={(event) => handleSelectRow(event, row[uniqueKey])}
                    padding="checkbox"
                    sx={{ ...borderStyle }}
                  >
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </TableCell>
                ) : (
                  <TableCell padding="checkbox" sx={{ ...borderStyle }} />
                )}

                {renderItemAvatar ? (
                  <TableCell padding="normal" sx={{ ...borderStyle }}>
                    {renderItemAvatar(row)}
                  </TableCell>
                ) : null}

                {columnKeys.map((ck, keyIndex) => {
                  const ActionComponent = headCells[keyIndex]?.ActionComponent;
                  const cellRenderProp = cellRenderProps[ck];
                  return (
                    <TableCell
                      key={ck as Key}
                      align={headCells.filter((v) => v.id === ck)[0].numeric ? "right" : "left"}
                      padding={headCells.filter((v) => v.id === ck)[0].disablePadding ? "none" : "normal"}
                      sx={{ ...borderStyle }}
                    >
                      {ActionComponent ? <ActionComponent item={row} /> : cellRenderProp?.(row) ?? row[ck]}
                    </TableCell>
                  );
                })}

                {enableRowItemActions ? (
                  <TableCell padding="none" sx={{ ...borderStyle }}>
                    <Stack direction="row" spacing={1}>
                      {renderRowActions?.(row)}
                    </Stack>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}

          {emptyRows > 0 && (
            <TableRow
              style={{
                height: (dense ? 33 : 53) * emptyRows,
              }}
            >
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
